import { useState } from 'react'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers'
import { getISOWeek, getISOWeeksInYear } from 'date-fns'

import { GeblokkeerdeWekenWarning } from '@components/GeblokkeerdeWekenWarning'
import type { Entity } from '@models/Entity'
import {
  beginningOfYearDateForISOWeek,
  // dateFromYearAndWeek,
  formatWeekYearToISOWeek,
  parseISO,
} from '@util/date-util'

import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import { StyledError } from '../../../../theme/components/StyledError'
import { usePatchAssignedBijlageForEntity } from '../../hooks/usePatchAssignedBijlageForEntity'
import type { AssignedBijlage } from '../../types/AssignedBijlage'

interface Props {
  bijlage: AssignedBijlage
  entity: Entity
  onClose: () => void
  geblokkeerdeWeken: GeblokkeerdeWeek[]
}

const MAX_WEEKS_IN_YEAR = 53
const WEEK_NUMBER_53 = 53

export const Edit = ({
  bijlage,
  entity,
  onClose,
  geblokkeerdeWeken,
}: Props) => {
  const {
    mutate: patchAssignedBijlageForEntity,
    error,
    isPending,
  } = usePatchAssignedBijlageForEntity()

  const {
    dueWeek,
    entity: { gebiedscode },
  } = bijlage

  const [jaar, setJaar] = useState<Date | null>(
    dueWeek ? beginningOfYearDateForISOWeek(dueWeek) : null,
  )
  const [week, setWeek] = useState<number | undefined>(
    dueWeek ? getISOWeek(parseISO(dueWeek)) : undefined,
  )
  const [errors, setErrors] = useState({})

  const validate = () => {
    let dueWeekError = !week || week > MAX_WEEKS_IN_YEAR || week < 1
    const dueJaarError = !jaar || jaar.getFullYear() < new Date().getFullYear()

    // Validate week 53
    if (!dueWeekError && !dueJaarError && week === WEEK_NUMBER_53) {
      dueWeekError = getISOWeeksInYear(jaar) !== MAX_WEEKS_IN_YEAR
    }

    setErrors({
      dueWeekError,
      dueJaarError,
    })

    return !(dueWeekError || dueJaarError)
  }
  const onClick = () => {
    if (!validate()) return

    patchAssignedBijlageForEntity(
      {
        entity,
        id: bijlage._id,
        dueWeek: formatWeekYearToISOWeek(jaar, week),
        status: bijlage.status,
      },
      { onSuccess: () => onClose() },
    )
  }

  const handleDelete = () => {
    patchAssignedBijlageForEntity(
      {
        entity,
        id: bijlage._id,
        status: bijlage.status,
      },
      {
        onSuccess: () => onClose(),
      },
    )
  }

  return (
    <>
      <DialogTitle>{`Aanpassen ${bijlage.documentsoort} ${
        bijlage.discipline || ''
      }`}</DialogTitle>

      <DialogContent>
        <GeblokkeerdeWekenWarning
          geblokkeerdeWeken={geblokkeerdeWeken}
          isoWeek={formatWeekYearToISOWeek(jaar, week || null)}
          gebiedscode={
            gebiedscode ||
            /* v8 ignore next - TODO: missing coverage -- @preserve */ ''
          }
        />

        <StyledError error={error} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 4,
          }}
        >
          <DatePicker
            views={['year']}
            disablePast
            label="Jaar"
            value={jaar}
            onChange={setJaar}
            openTo="year"
            slotProps={{
              field: { clearable: true },
              textField: {
                fullWidth: true,
                error: Boolean(errors['dueJaarError']),
                variant: 'standard',
              },
            }}
          />

          <TextField
            slotProps={{ htmlInput: { min: 1, max: 52 } }}
            fullWidth
            variant="standard"
            type="number"
            value={week || ''}
            label="Week"
            onChange={(e) => setWeek(Number(e.target.value))}
            error={errors['dueWeekError']}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Box sx={{ gap: 1, display: 'flex', flexGrow: 1 }}>
          {dueWeek && (
            <Button
              onClick={handleDelete}
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
            >
              Planning verwijderen
            </Button>
          )}
        </Box>

        <Box sx={{ gap: 1, display: 'flex' }}>
          <Button onClick={onClose} variant="text">
            Annuleren
          </Button>

          <Button
            onClick={onClick}
            variant="contained"
            disabled={isPending}
            startIcon={
              isPending ? <CircularProgress size={20} /> : <EditIcon />
            }
          >
            Planning wijzigen
          </Button>
        </Box>
      </DialogActions>
    </>
  )
}
